import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toPastTenseAction } from '../../../utils/actionLabel'
import {
  Button,
  message,
  Modal,
  Spin,
  Tabs,
} from '@surecompanies/core_components'
import EditOutlined from '@ant-design/icons/EditOutlined'
import PlanAndRateOverrideForm, {
  type PlanAndRateServerFieldError,
  apiPayloadToForm,
} from './PlanAndRateOverrideForm'
import OverrideHeaderChips from '../../../components/OverrideHeaderChips'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import type {
  CreatePlanAndRateOverrideRuleRequestDto,
  UpdatePlanAndRateOverrideRuleRequestDto,
} from '../../../api/dto/plan-and-rate-override-rules.dto'
import { PlanAndRateOverrideRuleStatus } from '../enums/plan-and-rate-override-rule.enum'
import { useGetPlanAndRateOverrideRule } from '../hooks/useGetPlanAndRateOverrideRule'
import { useTogglePlanAndRateOverrideRuleStatus } from '../hooks/useTogglePlanAndRateOverrideRuleStatus'
import { useUpdatePlanAndRateOverrideRule } from '../hooks/useUpdatePlanAndRateOverrideRule'
import {
  extractFieldErrors,
  getApiErrorMessage,
  getApiErrorStatus,
} from '../../../utils/apiErrors'
import OverrideHistoryTimeline from '../../../components/OverrideHistoryTimeline'
import DocumentSection from '../../../components/DocumentSection'
import { useEditDocumentStaging } from '../../../hooks/useEditDocumentStaging'
import { PLAN_AND_RATE_OVERRIDE_RULE_BASE } from '../../../api/plan-and-rate-override-rules-api'
import type { PlanAndRateViewMetadata } from './planAndRateOverrideForm.schema'

export type PlanAndRateOverrideModalMode = 'view' | 'edit'

interface Props {
  ruleId: string | null
  initialMode?: PlanAndRateOverrideModalMode
  onClose: () => void
  onModeChange?: (mode: PlanAndRateOverrideModalMode) => void
  onSaved?: (status: PlanAndRateOverrideRuleStatus) => void
  canEdit?: boolean
}

export default function PlanAndRateOverrideModal({
  ruleId,
  initialMode = 'view',
  onClose,
  onModeChange,
  onSaved,
  canEdit = true,
}: Props) {
  const open = ruleId !== null
  const [mode, setMode] = useState<PlanAndRateOverrideModalMode>(initialMode)
  const [activeTab, setActiveTab] = useState('details')
  const [serverErrors, setServerErrors] = useState<
    PlanAndRateServerFieldError[]
  >([])
  const {
    data: rule,
    isLoading,
    error,
  } = useGetPlanAndRateOverrideRule(ruleId ?? undefined)
  const { mutateAsync, isPending: isUpdatePending } =
    useUpdatePlanAndRateOverrideRule(ruleId ?? '')
  const { mutateAsync: toggleStatus, isPending: isTogglePending } =
    useTogglePlanAndRateOverrideRuleStatus()
  const isPending = isUpdatePending || isTogglePending
  const [isSubmitLocked, setIsSubmitLocked] = useState(false)
  const submitLockRef = useRef(false)
  const staging = useEditDocumentStaging({
    ruleId: ruleId ?? '',
    ruleBase: PLAN_AND_RATE_OVERRIDE_RULE_BASE,
  })
  const isBusy = isPending || isSubmitLocked || staging.isCommitting

  useEffect(() => {
    if (open) {
      setMode(initialMode)
      setActiveTab('details')
      setServerErrors([])
    }
  }, [open, initialMode, ruleId])

  const stagingDiscard = staging.discard
  useEffect(() => {
    stagingDiscard()
  }, [ruleId, stagingDiscard])

  const defaultValues = useMemo(() => {
    if (!rule) return undefined
    return apiPayloadToForm(rule)
  }, [rule])

  const metadata = useMemo<PlanAndRateViewMetadata | undefined>(() => {
    if (!rule) return undefined
    return {
      status: rule.status,
      createdAt: rule.createdAt,
      createdByEmail: rule.createdByEmail,
      updatedAt: rule.updatedAt,
      updatedByEmail: rule.updatedByEmail,
    }
  }, [rule])

  const closeModal = () => {
    if (isBusy) return
    if (staging.hasStagedChanges) {
      Modal.confirm({
        title: 'Discard unsaved document changes?',
        content:
          'You have pending uploads or removals that have not been saved. Discard them?',
        okText: 'Discard',
        okButtonProps: { danger: true },
        cancelText: 'Keep editing',
        onOk: () => {
          staging.discard()
          setServerErrors([])
          onClose()
        },
      })
      return
    }
    setServerErrors([])
    onClose()
  }

  const handleSwitchToEdit = () => {
    setServerErrors([])
    setMode('edit')
    onModeChange?.('edit')
  }

  const handleCancelEdit = closeModal

  const handleStatusChange = (toStatus: PlanAndRateOverrideRuleStatus) => {
    const actionLabel =
      toStatus === PlanAndRateOverrideRuleStatus.APPROVED
        ? 'Approve'
        : toStatus === PlanAndRateOverrideRuleStatus.REJECTED
          ? 'Reject'
          : 'Re-draft'
    Modal.confirm({
      title: `${actionLabel} override`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()} this override?`,
      okText: actionLabel,
      okButtonProps: {
        danger: toStatus === PlanAndRateOverrideRuleStatus.REJECTED,
      },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await toggleStatus({ id: ruleId ?? '', status: toStatus })
          message.success(`Override ${toPastTenseAction(actionLabel)}.`)
          if (rule) onSaved?.(toStatus)
          onClose()
        } catch {
          message.error(
            `Failed to ${actionLabel.toLowerCase()} override. Please try again.`
          )
        }
      },
    })
  }

  const handleApprove = () =>
    handleStatusChange(PlanAndRateOverrideRuleStatus.APPROVED)
  const handleReject = () =>
    handleStatusChange(PlanAndRateOverrideRuleStatus.REJECTED)
  const handleRedraft = () =>
    handleStatusChange(PlanAndRateOverrideRuleStatus.DRAFT)

  const handleSubmit = async (
    body: CreatePlanAndRateOverrideRuleRequestDto
  ) => {
    if (submitLockRef.current || isBusy) return
    submitLockRef.current = true
    setIsSubmitLocked(true)
    setServerErrors([])
    const updateBody: UpdatePlanAndRateOverrideRuleRequestDto = {
      params: body.params,
    }
    try {
      await mutateAsync(updateBody)
      try {
        await staging.commit()
      } catch (commitErr) {
        // eslint-disable-next-line no-console
        console.error('staged document commit failed', commitErr)
        message.error(
          'Override saved, but some document changes failed. Please retry.'
        )
        return
      }
      message.success('Override updated.')
      if (rule) onSaved?.(rule.status)
      onClose()
    } catch (err) {
      const status = getApiErrorStatus(err)
      if (status === 400 || status === 422) {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors.length > 0) {
          setServerErrors(fieldErrors)
          return
        }
        message.error(
          getApiErrorMessage(
            'Some fields look incorrect. Please review and try again.'
          )
        )
      } else {
        // eslint-disable-next-line no-console
        console.error('updatePlanAndRateOverrideRule failed', err)
        message.error('An error occurred. Please try again.')
      }
    } finally {
      submitLockRef.current = false
      setIsSubmitLocked(false)
    }
  }

  const isViewMode = mode === 'view'
  const isApproved = rule?.status === PlanAndRateOverrideRuleStatus.APPROVED
  const isDraft = rule?.status === PlanAndRateOverrideRuleStatus.DRAFT

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className="bodyPlaceholder">
        <Spin />
      </div>
    )
  } else if (error || !rule) {
    body = (
      <div className="bodyPlaceholder">
        <p>Override not found.</p>
      </div>
    )
  } else if (isViewMode) {
    body = (
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <PlanAndRateOverrideForm
                mode="view"
                defaultValues={defaultValues}
                extraContent={
                  ruleId ? (
                    <DocumentSection
                      ruleId={ruleId}
                      readOnly
                      ruleBase={PLAN_AND_RATE_OVERRIDE_RULE_BASE}
                    />
                  ) : null
                }
              />
            ),
          },
          {
            key: 'history',
            label: 'History',
            children: ruleId ? (
              <OverrideHistoryTimeline
                ruleId={ruleId}
                ruleBase={PLAN_AND_RATE_OVERRIDE_RULE_BASE}
                displayVariant="planAndRate"
              />
            ) : null,
          },
        ]}
      />
    )
  } else {
    body = (
      <PlanAndRateOverrideForm
        mode="edit"
        defaultValues={defaultValues}
        metadata={metadata}
        serverErrors={serverErrors}
        isSubmitting={isBusy}
        onSubmit={handleSubmit}
        onCancel={handleCancelEdit}
        extraContent={
          ruleId ? (
            <DocumentSection
              ruleId={ruleId}
              staging={staging}
              ruleBase={PLAN_AND_RATE_OVERRIDE_RULE_BASE}
            />
          ) : null
        }
      />
    )
  }

  const titleNode = (
    <div className="titleRow">
      <span className="titleLeft">
        <span className="titleText">
          {isViewMode
            ? 'Review Plan & Rate Override'
            : 'Edit Plan & Rate Override'}
        </span>
        {rule && (
          <OverrideHeaderChips label="Plans & Rates" status={rule.status} />
        )}
      </span>
    </div>
  )

  return (
    <Modal
      title={titleNode}
      open={open}
      onCancel={closeModal}
      width={960}
      destroyOnClose
      maskClosable={!isBusy}
      footer={
        isViewMode && rule && activeTab === 'details' ? (
          <div className="footerRow">
            {metadata && <OverrideAuditStrip metadata={metadata} />}
            <div className="footerActions">
              <Button type="default" onClick={closeModal} disabled={isBusy}>
                Close
              </Button>
              {canEdit && (
                <Button
                  type="default"
                  onClick={handleSwitchToEdit}
                  disabled={isBusy}
                >
                  <EditOutlined /> Edit
                </Button>
              )}
              {canEdit && (
                <>
                  {isApproved && (
                    <Button danger onClick={handleReject} loading={isPending}>
                      Reject
                    </Button>
                  )}
                  {!isApproved && (
                    <Button
                      type="primary"
                      onClick={handleApprove}
                      loading={isPending}
                    >
                      Approve
                    </Button>
                  )}
                  {!isDraft && (
                    <Button onClick={handleRedraft} loading={isPending}>
                      Re-draft
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {body}
    </Modal>
  )
}
